import { Alert, Button, SegmentedControl, Select, Stack, Text, TextInput } from '@mantine/core';
import { IconMicrophone, IconPlayerStopFilled } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useFetchMembers from 'src/Hooks/Queries/useFetchMembers';
import useFetchUser from 'src/Hooks/Queries/useFetchUser';
import {
  isVoiceRecordingSupported,
  useVoiceRecorder,
  VoiceRecording,
} from 'src/Hooks/Shared/useVoiceRecorder';
import { formatVoiceDuration } from 'src/Utils/memoryDate';

import styles from './VoiceStoryCapture.module.scss';

/** 10 minutes per story (Alexis, 02/11/2026). */
export const MAX_STORY_SECONDS = 600;

type SpeakerMode = 'me' | 'member' | 'other';

/** A recorded story and who told it, not sent yet. */
export interface PendingStory {
  recording: VoiceRecording;
  speakerId?: string;
  speakerName?: string;
}

interface Props {
  /** Called with the story once it is recorded and its storyteller known, null otherwise. */
  onChange: (story: PendingStory | null) => void;
}

/**
 * "Qui raconte ?" then a big microphone. The audio only lives in the browser until the
 * parent sends it, so a network error never loses a story.
 */
const VoiceStoryCapture = ({ onChange }: Props) => {
  const { t } = useTranslation();
  const { data: user } = useFetchUser();
  const { data: membersData } = useFetchMembers();

  const [speakerMode, setSpeakerMode] = useState<SpeakerMode>('me');
  const [speakerId, setSpeakerId] = useState<string | null>(null);
  const [speakerName, setSpeakerName] = useState('');
  const [recording, setRecording] = useState<VoiceRecording | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { isRecording, elapsedSeconds, start, stop } = useVoiceRecorder({
    maxDurationSeconds: MAX_STORY_SECONDS,
    onRecorded: setRecording,
  });

  const memberOptions = useMemo(
    () =>
      (membersData?.members ?? [])
        .filter(member => member.user && member.user.id !== user?.id)
        .map(member => ({
          value: member.user.id,
          // First name first; a member without one falls back to the name the app shows elsewhere.
          label:
            member.user.firstName?.trim() ||
            `${member.user.firstName ?? ''} ${member.user.lastName ?? ''}`.trim() ||
            t('memories.recorder.unnamedMember', 'Membre sans nom'),
        })),
    [membersData, user?.id, t]
  );

  const speakerIsValid =
    speakerMode === 'me' ||
    (speakerMode === 'member' && !!speakerId) ||
    (speakerMode === 'other' && speakerName.trim().length > 0);

  useEffect(() => {
    onChange(
      recording && speakerIsValid
        ? {
            recording,
            speakerId: speakerMode === 'member' ? (speakerId ?? undefined) : undefined,
            speakerName: speakerMode === 'other' ? speakerName.trim() : undefined,
          }
        : null
    );
    // onChange is a parent setter; re-run only when the story itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording, speakerIsValid, speakerMode, speakerId, speakerName]);

  const handleStart = async () => {
    setError(null);
    try {
      await start();
    } catch (startError) {
      const name = (startError as DOMException)?.name;
      setError(
        name === 'NotAllowedError' || name === 'SecurityError'
          ? t(
              'voiceDictation.errors.denied',
              "L'accès au micro est bloqué. Autorise-le dans les réglages du navigateur (icône à gauche de l'adresse du site), puis réessaie."
            )
          : t('voiceDictation.errors.noMicrophone', 'Aucun micro n’a été trouvé sur cet appareil.')
      );
    }
  };

  if (!isVoiceRecordingSupported()) {
    return (
      <Alert color="gray">
        {t(
          'memories.recorder.unsupported',
          'Ce navigateur ne sait pas enregistrer la voix. Essayez depuis un téléphone ou un autre navigateur.'
        )}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <div className={styles.recorder}>
        {!recording && (
          <Button
            size="xl"
            h={96}
            radius="xl"
            color={isRecording ? 'red' : undefined}
            leftSection={
              isRecording ? <IconPlayerStopFilled size={32} /> : <IconMicrophone size={32} />
            }
            onClick={isRecording ? stop : handleStart}
          >
            {isRecording
              ? t('memories.recorder.stop', 'J’ai fini')
              : t('memories.recorder.start', 'Appuyer pour raconter')}
          </Button>
        )}
        {isRecording && (
          <Text className={styles.timer}>
            {formatVoiceDuration(elapsedSeconds)} / {formatVoiceDuration(MAX_STORY_SECONDS)}
          </Text>
        )}
        {recording && (
          <>
            <Text className={styles.timer}>
              {t('memories.recorder.recorded', 'Récit enregistré : {{duration}}', {
                duration: formatVoiceDuration(recording.durationSeconds),
              })}
            </Text>
            <Button variant="outline" size="md" onClick={() => setRecording(null)}>
              {t('memories.recorder.restart', 'Recommencer')}
            </Button>
          </>
        )}
      </div>

      <Stack gap="xs">
        <Text fw={500}>{t('memories.recorder.whoSpeaks', 'Qui raconte ?')}</Text>
        <SegmentedControl
          fullWidth
          size="md"
          value={speakerMode}
          onChange={value => setSpeakerMode(value as SpeakerMode)}
          data={[
            { value: 'me', label: t('memories.recorder.me', 'Moi') },
            { value: 'member', label: t('memories.recorder.member', 'Un membre') },
            { value: 'other', label: t('memories.recorder.other', 'Quelqu’un d’autre') },
          ]}
        />
        {speakerMode === 'member' && (
          <Select
            size="md"
            data={memberOptions}
            value={speakerId}
            onChange={setSpeakerId}
            searchable
            placeholder={t('memories.recorder.chooseMember', 'Choisir un membre')}
          />
        )}
        {speakerMode === 'other' && (
          <TextInput
            size="md"
            value={speakerName}
            onChange={event => setSpeakerName(event.currentTarget.value)}
            maxLength={100}
            placeholder={t('memories.recorder.otherPlaceholder', 'Mamie Odette, tonton Jean…')}
          />
        )}
        {recording && !speakerIsValid && (
          <Text size="sm" c="dimmed">
            {t('memories.recorder.speakerMissing', 'Indiquez qui raconte pour ajouter le récit.')}
          </Text>
        )}
      </Stack>

      {error && <Alert color="red">{error}</Alert>}
    </Stack>
  );
};

export default VoiceStoryCapture;
